// The preschool calendar: per academic year the periods of the Costs sheet's Calendar tab (two halves per term,
// half terms and holidays, each with official and actual dates), bank holidays and the preschool's own closures,
// and what invoicing derives from it: open days per term, and per child the full weeks and extra hours their
// sessions come to (the manual "2 weeks and 4 extra days" method, done exactly).
//
// Pure and TS 4.9 compatible: shared by the teacher portal, the functions and the preschool invoice generator
// Apps Script (one-off import of the Calendar tab).

export type PeriodKey =
  | "autumn1" | "autumnHalf" | "autumn2" | "christmas"
  | "spring1" | "springHalf" | "spring2" | "easter"
  | "summer1" | "summerHalf" | "summer2" | "summerHoliday";

export interface DateRange { start: string; end: string }

export interface CalendarPeriod {
  key: PeriodKey;
  /** The council's / schools' dates. */
  official: DateRange | null;
  /** When the preschool is actually open (terms) or closed (holidays). Invoicing uses these. */
  actual: DateRange | null;
  /** Holidays only: when the Holiday Club runs (planned; its form's days and bookings show alongside). */
  club?: DateRange | null;
}

export interface Closure { date: string; reason: string }

/** preschoolCalendar/{yearId}, e.g. preschoolCalendar/2026-27 (September 2026 to August 2027). */
export interface PreschoolCalendar {
  id: string;
  periods: CalendarPeriod[];
  /** Extra days the preschool is closed during term (INSET, training, ...). */
  closures: Closure[];
  /** Bank holidays the preschool opens anyway. */
  openOnBankHolidays: string[];
}

export const PERIODS: { key: PeriodKey; name: string; term: "autumn" | "spring" | "summer" | null }[] = [
  { key: "autumn1", name: "Autumn 1", term: "autumn" },
  { key: "autumnHalf", name: "Autumn half term", term: null },
  { key: "autumn2", name: "Autumn 2", term: "autumn" },
  { key: "christmas", name: "Christmas holiday", term: null },
  { key: "spring1", name: "Spring 1", term: "spring" },
  { key: "springHalf", name: "Spring half term", term: null },
  { key: "spring2", name: "Spring 2", term: "spring" },
  { key: "easter", name: "Easter holiday", term: null },
  { key: "summer1", name: "Summer 1", term: "summer" },
  { key: "summerHalf", name: "Summer half term", term: null },
  { key: "summer2", name: "Summer 2", term: "summer" },
  { key: "summerHoliday", name: "Summer holiday", term: null },
];

/**
 * England and Wales bank holidays (gov.uk). Extend the list each year; days missing here can still be added as
 * closures.
 */
export const BANK_HOLIDAYS: { [date: string]: string } = {
  "2023-01-02": "New Year's Day (substitute)", "2023-04-07": "Good Friday", "2023-04-10": "Easter Monday",
  "2023-05-01": "Early May bank holiday", "2023-05-08": "Coronation bank holiday", "2023-05-29": "Spring bank holiday",
  "2023-08-28": "Summer bank holiday", "2023-12-25": "Christmas Day", "2023-12-26": "Boxing Day",
  "2024-01-01": "New Year's Day", "2024-03-29": "Good Friday", "2024-04-01": "Easter Monday",
  "2024-05-06": "Early May bank holiday", "2024-05-27": "Spring bank holiday", "2024-08-26": "Summer bank holiday",
  "2024-12-25": "Christmas Day", "2024-12-26": "Boxing Day",
  "2025-01-01": "New Year's Day", "2025-04-18": "Good Friday", "2025-04-21": "Easter Monday",
  "2025-05-05": "Early May bank holiday", "2025-05-26": "Spring bank holiday", "2025-08-25": "Summer bank holiday",
  "2025-12-25": "Christmas Day", "2025-12-26": "Boxing Day",
  "2026-01-01": "New Year's Day", "2026-04-03": "Good Friday", "2026-04-06": "Easter Monday",
  "2026-05-04": "Early May bank holiday", "2026-05-25": "Spring bank holiday", "2026-08-31": "Summer bank holiday",
  "2026-12-25": "Christmas Day", "2026-12-28": "Boxing Day (substitute)",
  "2027-01-01": "New Year's Day", "2027-03-26": "Good Friday", "2027-03-29": "Easter Monday",
  "2027-05-03": "Early May bank holiday", "2027-05-31": "Spring bank holiday", "2027-08-30": "Summer bank holiday",
  "2027-12-27": "Christmas Day (substitute)", "2027-12-28": "Boxing Day (substitute)",
  "2028-01-03": "New Year's Day (substitute)", "2028-04-14": "Good Friday", "2028-04-17": "Easter Monday",
  "2028-05-01": "Early May bank holiday", "2028-05-29": "Spring bank holiday", "2028-08-28": "Summer bank holiday",
  "2028-12-25": "Christmas Day", "2028-12-26": "Boxing Day",
};

const DAY = 86400000;
const toTime = (iso: string) => Date.parse(`${iso}T12:00:00Z`);
const toIso = (time: number) => new Date(time).toISOString().slice(0, 10);
/** 0 = Monday ... 6 = Sunday */
export const weekdayOf = (iso: string) => (new Date(toTime(iso)).getUTCDay() + 6) % 7;
export const mondayOf = (iso: string) => toIso(toTime(iso) - weekdayOf(iso) * DAY);

export const datesBetween = (range: DateRange) => {
  const dates: string[] = [];
  for (let t = toTime(range.start); t <= toTime(range.end) && dates.length < 400; t += DAY) dates.push(toIso(t));
  return dates;
};

const weekdaysIn = (range: DateRange) => datesBetween(range).filter((d) => weekdayOf(d) < 5);

/** "2026-27" for the year starting September 2026. */
export const academicYearId = (startYear: number) => `${startYear}-${String((startYear + 1) % 100).padStart(2, "0")}`;

/** The academic year a term id ("2027-spring") belongs to: autumn starts the year. */
export const academicYearOfTerm = (termId: string) => {
  const [year, season] = termId.split("-");
  const y = parseInt(year, 10);
  return academicYearId(season === "autumn" ? y : y - 1);
};

export const termIdOf = (calendarId: string, term: "autumn" | "spring" | "summer") => {
  const start = parseInt(calendarId.slice(0, 4), 10);
  return `${term === "autumn" ? start : start + 1}-${term}`;
};

export const emptyCalendar = (id: string): PreschoolCalendar => ({
  id,
  periods: PERIODS.map((p) => ({ key: p.key, official: null, actual: null })),
  closures: [],
  openOnBankHolidays: [],
});

export interface ClosedDay { date: string; reason: string; bankHoliday: boolean }

/** Weekdays inside the actual term periods that the preschool is closed. */
export const closedDays = (calendar: PreschoolCalendar): ClosedDay[] => {
  const inTerm = new Set(calendar.periods
    .filter((p) => PERIODS.find((d) => d.key === p.key)!.term && p.actual)
    .reduce((all, p) => all.concat(weekdaysIn(p.actual!)), [] as string[]));
  const days: ClosedDay[] = [];
  for (const date of Object.keys(BANK_HOLIDAYS)) {
    if (inTerm.has(date) && calendar.openOnBankHolidays.indexOf(date) < 0) days.push({ date, reason: BANK_HOLIDAYS[date], bankHoliday: true });
  }
  for (const closure of calendar.closures) {
    if (weekdayOf(closure.date) < 5 && !days.some((d) => d.date === closure.date)) days.push({ ...closure, bankHoliday: false });
  }
  return days.sort((a, b) => a.date.localeCompare(b.date));
};

/** Open weekdays of a period (or of every period with this term), closures removed. */
export const openDays = (calendar: PreschoolCalendar, keys: PeriodKey[]) => {
  const closed = new Set(closedDays(calendar).map((d) => d.date));
  return calendar.periods
    .filter((p) => keys.indexOf(p.key) >= 0 && p.actual)
    .reduce((all, p) => all.concat(weekdaysIn(p.actual!)), [] as string[])
    .filter((d) => !closed.has(d))
    .sort();
};

export const termPeriodKeys = (term: "autumn" | "spring" | "summer") => PERIODS.filter((p) => p.term === term).map((p) => p.key);

/** Open days of the term with this id ("2026-autumn"), or null when the calendar has no dates for it. */
export const termOpenDays = (calendar: PreschoolCalendar, termId: string): string[] | null => {
  const term = termId.split("-")[1] as "autumn" | "spring" | "summer";
  const keys = termPeriodKeys(term);
  if (!calendar.periods.some((p) => keys.indexOf(p.key) >= 0 && p.actual)) return null;
  return openDays(calendar, keys);
};

/** Weekdays of a planned Holiday Club range, bank holidays excluded. */
export const clubDaysIn = (range: DateRange | null | undefined) =>
  range && range.start && range.end && range.start <= range.end ? weekdaysIn(range).filter((d) => !BANK_HOLIDAYS[d]) : [];

/** Weekdays in a range, as the sheet's NETWORKDAYS counted them (closures not removed). */
export const weekdayCount = (range: DateRange | null) => (range ? weekdaysIn(range).length : 0);

export const weeksAndDays = (days: number) => {
  const weeks = Math.floor(days / 5);
  const rest = days % 5;
  return `${weeks} week${weeks === 1 ? "" : "s"}${rest ? ` ${rest} day${rest === 1 ? "" : "s"}` : ""}`;
};

/** First and last open day of a term, for its name and dates. */
export const termSpan = (days: string[]) => (days.length ? { start: days[0], end: days[days.length - 1] } : null);

export interface ChildTerm {
  /** Weeks with every weekday open (from the child's start). */
  fullWeeks: number;
  fundedPerWeek: number;
  privatePerWeek: number;
  /** Hours on the open days of part-weeks (bank holidays, term edges, a mid-term start), split week by week. */
  fundedExtra: number;
  privateExtra: number;
  /** Open morning-only days in part-weeks (for lunch). */
  partWeekMorningOnlyDays: number;
  morningOnlyPerWeek: number;
  partWeeks: number;
}

/**
 * A child's term from their weekly sessions on the open days. Funding covers up to the entitlement per week, the
 * rest is private; in part-weeks the same cap applies to the hours of that week.
 *
 * @param hoursByWeekday hours per weekday, Monday first (e.g. [6, 3, 3, 3, 3])
 * @param morningOnly weekdays the child comes for the morning only (lunch possible)
 */
export const childTerm = (
  open: string[],
  hoursByWeekday: number[],
  entitlementPerWeek: number,
  startDate: string | null,
  morningOnly: boolean[] = [false, false, false, false, false]
): ChildTerm => {
  const days = startDate ? open.filter((d) => d >= startDate.slice(0, 10)) : open;
  const weeks: { [monday: string]: string[] } = {};
  for (const day of days) (weeks[mondayOf(day)] = weeks[mondayOf(day)] || []).push(day);
  const perWeek = hoursByWeekday.reduce((sum, h) => sum + h, 0);
  const fundedPerWeek = Math.min(perWeek, entitlementPerWeek);
  const result: ChildTerm = {
    fullWeeks: 0, fundedPerWeek, privatePerWeek: perWeek - fundedPerWeek, fundedExtra: 0, privateExtra: 0,
    partWeekMorningOnlyDays: 0, morningOnlyPerWeek: morningOnly.filter(Boolean).length, partWeeks: 0,
  };
  for (const monday of Object.keys(weeks)) {
    const weekDays = weeks[monday];
    if (weekDays.length === 5) {
      result.fullWeeks++;
      continue;
    }
    result.partWeeks++;
    const hours = weekDays.reduce((sum, d) => sum + hoursByWeekday[weekdayOf(d)], 0);
    const funded = Math.min(hours, entitlementPerWeek);
    result.fundedExtra += funded;
    result.privateExtra += hours - funded;
    result.partWeekMorningOnlyDays += weekDays.filter((d) => morningOnly[weekdayOf(d)]).length;
  }
  return result;
};

/** Hours per weekday (Monday first) and morning-only weekdays from "Monday - Morning, Monday - Afternoon, ...". */
export const weeklyPattern = (enrolment: string, hoursPerSession: number) => {
  const names = ["monday", "tuesday", "wednesday", "thursday", "friday"];
  const am = [false, false, false, false, false];
  const pm = [false, false, false, false, false];
  for (const part of (enrolment || "").split(",")) {
    const [day, time] = part.split("-").map((s) => (s || "").trim().toLowerCase());
    const index = names.indexOf(day);
    if (index < 0) continue;
    if (time === "morning") am[index] = true;
    if (time === "afternoon") pm[index] = true;
  }
  return {
    hoursByWeekday: names.map((_, i) => ((am[i] ? 1 : 0) + (pm[i] ? 1 : 0)) * hoursPerSession),
    morningOnly: names.map((_, i) => am[i] && !pm[i]),
  };
};

// ---------------------------------------------------------------------------------------------------------------
// Import of the Costs sheet's Calendar tab: blocks of rows "Year | Term | Official start | Official end | Weeks |
// | Actual start | Actual end | Weeks", twelve periods per academic year in the order of PERIODS.

const SHEET_NAMES: { [name: string]: PeriodKey[] } = {
  "autumn": ["autumn1", "autumn2"], "autumn half term": ["autumnHalf"], "christmas holiday": ["christmas"],
  "spring": ["spring1", "spring2"], "spring half term": ["springHalf"], "easter holiday": ["easter"],
  "summer": ["summer1", "summer2"], "summer half term": ["summerHalf"], "summer holiday": ["summerHoliday"],
};

const sheetDate = (value: unknown): string | null => {
  if (value instanceof Date && !isNaN(value.getTime())) {
    return new Date(value.getTime() - value.getTimezoneOffset() * 60000).toISOString().slice(0, 10);
  }
  if (typeof value === "number" && value > 30000) return toIso(Date.UTC(1899, 11, 30) + value * DAY + DAY / 2);
  return null;
};

/** Calendars from the Calendar tab's values. Rows without actual dates (the old three-row summary) are skipped. */
export const calendarsFromSheet = (rows: unknown[][]): PreschoolCalendar[] => {
  const calendars: { [id: string]: PreschoolCalendar } = {};
  let current: PreschoolCalendar | null = null;
  for (const row of rows) {
    const name = String(row[1] || "").trim().toLowerCase();
    const official = { start: sheetDate(row[2]), end: sheetDate(row[3]) };
    const actual = { start: sheetDate(row[6]), end: sheetDate(row[7]) };
    const keys = SHEET_NAMES[name];
    if (!keys || !actual.start || !actual.end) continue;
    if (name === "autumn" && (!current || current.periods.find((p) => p.key === "autumn2")!.actual)) {
      const id = academicYearId(parseInt(actual.start.slice(0, 4), 10));
      current = calendars[id] = calendars[id] || emptyCalendar(id);
    }
    if (!current) continue;
    const period = keys.map((k) => current!.periods.find((p) => p.key === k)!).find((p) => !p.actual);
    if (!period) continue;
    period.actual = { start: actual.start, end: actual.end };
    period.official = official.start && official.end ? { start: official.start, end: official.end } : null;
  }
  return Object.keys(calendars).sort().map((id) => calendars[id]);
};
